/**
 * Agent Service
 * Manages MCP-based agents (Copilot, Azure AI Agent Framework)
 */

import type {
  Agent,
  AgentInstallRequest,
  AgentExecutionContext,
  AgentResponse,
  IAgentService,
} from '@/types/agent.types';
import { getBackendUrl as getRuntimeBackendUrl } from '@/config/runtimeConfig';
import { getBackendAuth, getBackendHeaders } from './backendAuth';

// Use dedicated auth database URL (separate from chat storage)
/**
 * Get backend URL with priority:
 * 1. Runtime config (container startup injection - HIGHEST PRIORITY)
 * 2. Build-time env vars (development fallback)
 * 3. AdminContext localStorage (user override for testing only - LOWEST PRIORITY)
 */
function getBackendUrl(): string {
  // Priority 1 & 2: Runtime config (container env vars) > Build-time env vars
  const runtimeUrl = getRuntimeBackendUrl();
  if (runtimeUrl) {
    return runtimeUrl;
  }

  // Priority 3: AdminContext localStorage (fallback for testing/dev only)
  try {
    const adminConfigStr = localStorage.getItem('stellar_admin_config');
    if (adminConfigStr) {
      const adminConfig = JSON.parse(adminConfigStr);
      if (adminConfig.authDatabase?.backendUrl) {
        console.warn('[agentService] ⚠️ Using backend URL from localStorage (low priority):', adminConfig.authDatabase.backendUrl);
        return adminConfig.authDatabase.backendUrl;
      }
    }
  } catch (error) {
    console.warn('[agentService] Failed to load backend URL from admin config:', error);
  }

  // Final fallback
  return 'http://localhost:8000/api';
}

/**
 * Backend API-based agent service
 * Stores agents in PostgreSQL via backend API
 */
class AgentService implements IAgentService {
  private agents: Agent[] = [];
  private isBackendAvailable = false;

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const auth = await getBackendAuth();
    const isSso = !!auth && !!auth.identity;

    // For Entra ID auth, send the subset of admin-configured group IDs
    // that the user is a member of so the backend can resolve role even
    // when the JWT's groups claim is stripped (which Entra does for
    // users with >200 group memberships, falling back to a _claim_names
    // indirection that the backend doesn't follow).
    //
    // We send only the *mapped* subset rather than all of the user's
    // groups: customers with 4000+ memberships would otherwise produce a
    // ~150 KB X-User-Groups header that exceeds the 8–16 KB limit
    // enforced by most reverse proxies and silently get dropped, leaving
    // backend role resolution stuck on the 'user' default.
    const extra: Record<string, string> = {};
    if (isSso) {
      try {
        const [{ checkUserMappedGroups }, { getPublicConfig }] = await Promise.all([
          import('./graphService'),
          import('./publicConfigService'),
        ]);
        const cfg = await getPublicConfig();
        const mappedIds = cfg.entraGroupMappings.map((m) => m.groupId);
        if (mappedIds.length > 0) {
          const userMapped = await checkUserMappedGroups(mappedIds);
          if (userMapped.length > 0) {
            extra['X-User-Groups'] = userMapped.join(',');
          }
        }
      } catch (groupError) {
        console.warn('[agentService:getAuthHeaders] Failed to fetch user groups:', groupError);
        // Non-fatal — continue without the groups header.
      }
    }

    return getBackendHeaders(extra);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  }

  async initialize(options: { includeConfig?: boolean } = {}): Promise<void> {
    await this.loadAgents(options);
  }

  private async loadAgents(
    options: { includeConfig?: boolean } = {}
  ): Promise<void> {
    try {
      // Try to load from backend first
      const backendAvailable = await this.checkBackendAvailability();
      console.log('[agentService] Backend availability:', backendAvailable);

      if (backendAvailable) {
        this.isBackendAvailable = true;
        const authHeaders = await this.getAuthHeaders();
        const authToken = authHeaders.Authorization?.replace('Bearer ', '');
        // Phase 3 B-9: the backend defaults to a LIGHT shape. Caller opts
        // into the heavy shape via { includeConfig: true } when downstream
        // consumers iterate the agents array and read `config` /
        // `systemPrompt` / `suggestedPrompts` / `capabilities` / `metadata`
        // off each row (AgentCard, AgentSelector, AgentConfigurationEditor).
        const query = options.includeConfig ? '?include=config' : '';
        const requestUrl = `${getBackendUrl()}/agents${query}`;
        console.log('[agentService] === REQUEST ===');
        console.log('[agentService] URL:', requestUrl);
        console.log('[agentService] Method: GET');
        console.log('[agentService] Headers:', {
          'Content-Type': authHeaders['Content-Type'],
          Authorization: authToken ? `Bearer ${authToken.substring(0, 20)}...${authToken.substring(authToken.length - 20)}` : 'none',
        });

        const response = await fetch(requestUrl, {
          headers: authHeaders,
        });

        console.log('[agentService] === RESPONSE ===');
        console.log('[agentService] Status:', response.status, response.statusText);
        console.log('[agentService] OK:', response.ok);

        // Clone response to read body for logging without consuming it
        // Check if clone() exists (may not be available in test environments)
        if (typeof response.clone === 'function') {
          try {
            const responseClone = response.clone();
            const responseBody = await responseClone.json();
            console.log('[agentService] Response body:', responseBody);
          } catch {
            console.log('[agentService] Response body: (not JSON)');
          }
        } else {
          console.log('[agentService] Response body: (clone not available - likely test environment)');
        }

        if (response.ok) {
          const backendAgents = await this.handleResponse<Agent[]>(response);
          console.log('[agentService] Loaded agents from backend:', backendAgents.length);
          this.agents = backendAgents.map((agent: Agent) => ({
            ...agent,
            installedAt: new Date(agent.installedAt),
            updatedAt: new Date(agent.updatedAt),
          }));

          // Do NOT cache in localStorage when backend is available
          // Agents should always come from backend, not localStorage
          // This ensures users see the correct agent state (active/inactive)
          // and admin configuration is respected

          // If no agents exist in backend, return empty array
          // Don't auto-initialize - admin must create agents
          if (this.agents.length === 0) {
            console.log('No agents found in backend. Admin must create agents.');
          }

          return;
        } else {
          // Log the error response
          const errorText = await response.text();
          console.error('[agentService] Failed to load agents from backend:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
          });
        }
      }

      // NO localStorage fallback - backend is REQUIRED for agents
      console.error('[agentService] Backend not available - agents cannot be loaded');
      this.isBackendAvailable = false;
      this.agents = [];
      throw new Error('Backend unavailable - cannot load agents. Please check backend connection.');
    } catch (error) {
      console.error('[agentService] Failed to load agents:', error);
      this.isBackendAvailable = false;
      this.agents = [];
      throw error;
    }
  }

  private async checkBackendAvailability(): Promise<boolean> {
    try {
      const response = await fetch(`${getBackendUrl().replace('/api', '')}/health`, {
        method: 'GET',
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // REMOVED: All localStorage-related code (migrations, saveAgents, initializeDefaultAgents)
  // Agents are managed exclusively via backend API
  // Admin must create agents through the backend - no default agents or localStorage fallback

  async getAllAgents(): Promise<Agent[]> {
    return [...this.agents].sort((a, b) => {
      if (a.isDefault) return -1;
      if (b.isDefault) return 1;
      return b.updatedAt.getTime() - a.updatedAt.getTime();
    });
  }

  async getAgent(id: string): Promise<Agent | null> {
    // Try backend first if available
    if (this.isBackendAvailable) {
      try {
        const response = await fetch(`${getBackendUrl()}/agents/${id}`, {
          headers: await this.getAuthHeaders(),
        });

        if (response.ok) {
          const agent = await this.handleResponse<Agent>(response);
          return {
            ...agent,
            installedAt: new Date(agent.installedAt),
            updatedAt: new Date(agent.updatedAt),
          };
        }
      } catch (error) {
        console.error('Failed to get agent from backend:', error);
      }
    }

    // Fallback to local cache
    return this.agents.find((agent) => agent.id === id) || null;
  }

  async installAgent(request: AgentInstallRequest): Promise<Agent> {
    // Backend is REQUIRED for agent installation
    if (!this.isBackendAvailable) {
      throw new Error('Backend unavailable - cannot install agents. Please check backend connection.');
    }

    try {
      const response = await fetch(`${getBackendUrl()}/agents`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(request),
      });

      if (response.ok) {
        const agent = await this.handleResponse<Agent>(response);
        const agentWithDates = {
          ...agent,
          installedAt: new Date(agent.installedAt),
          updatedAt: new Date(agent.updatedAt),
        };

        this.agents.push(agentWithDates);
        return agentWithDates;
      }

      throw new Error('Failed to install agent via backend');
    } catch (error) {
      console.error('Failed to install agent via backend:', error);
      throw error;
    }
  }

  async updateAgent(id: string, updates: Partial<Agent>): Promise<void> {
    // Backend is REQUIRED for agent updates
    if (!this.isBackendAvailable) {
      throw new Error('Backend unavailable - cannot update agents. Please check backend connection.');
    }

    try {
      const response = await fetch(`${getBackendUrl()}/agents/${id}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (response.ok) {
        const updatedAgent = await this.handleResponse<Agent>(response);
        const index = this.agents.findIndex((agent) => agent.id === id);
        if (index !== -1) {
          this.agents[index] = {
            ...updatedAgent,
            installedAt: new Date(updatedAgent.installedAt),
            updatedAt: new Date(updatedAgent.updatedAt),
          };
        }
        return;
      }

      // Get detailed error from backend
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      const errorMessage = errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      console.error('[agentService] Backend error details:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
        updates,
      });
      throw new Error(`Failed to update agent: ${errorMessage}`);
    } catch (error) {
      console.error('Failed to update agent via backend:', error);
      throw error;
    }
  }

  async duplicateAgent(id: string, name?: string): Promise<Agent> {
    if (!this.isBackendAvailable) {
      throw new Error(
        'Backend unavailable - cannot duplicate agents. Please check backend connection.'
      );
    }

    try {
      const body =
        typeof name === 'string' && name.trim().length > 0
          ? JSON.stringify({ name: name.trim() })
          : JSON.stringify({});

      const response = await fetch(`${getBackendUrl()}/agents/${id}/duplicate`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body,
      });

      if (response.ok) {
        const agent = await this.handleResponse<Agent>(response);
        const agentWithDates = {
          ...agent,
          installedAt: new Date(agent.installedAt),
          updatedAt: new Date(agent.updatedAt),
        };
        this.agents.push(agentWithDates);
        return agentWithDates;
      }

      const errorData = await response
        .json()
        .catch(() => ({ error: 'Unknown error' }));
      const errorMessage =
        errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(`Failed to duplicate agent: ${errorMessage}`);
    } catch (error) {
      console.error('Failed to duplicate agent via backend:', error);
      throw error;
    }
  }

  async deleteAgent(id: string): Promise<void> {
    const agent = this.agents.find((a) => a.id === id);
    if (agent?.isDefault) {
      throw new Error('Cannot delete default agent');
    }

    // Backend is REQUIRED for agent deletion
    if (!this.isBackendAvailable) {
      throw new Error('Backend unavailable - cannot delete agents. Please check backend connection.');
    }

    try {
      const response = await fetch(`${getBackendUrl()}/agents/${id}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders(),
      });

      if (response.ok) {
        this.agents = this.agents.filter((agent) => agent.id !== id);
        return;
      }

      throw new Error('Failed to delete agent via backend');
    } catch (error) {
      console.error('Failed to delete agent via backend:', error);
      throw error;
    }
  }

  async activateAgent(id: string): Promise<void> {
    // Backend is REQUIRED for agent activation
    if (!this.isBackendAvailable) {
      throw new Error('Backend unavailable - cannot activate agents. Please check backend connection.');
    }

    try {
      const response = await fetch(`${getBackendUrl()}/agents/${id}/activate`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
      });

      if (response.ok) {
        const updatedAgent = await this.handleResponse<Agent>(response);
        const index = this.agents.findIndex((agent) => agent.id === id);
        if (index !== -1) {
          this.agents[index] = {
            ...updatedAgent,
            installedAt: new Date(updatedAgent.installedAt),
            updatedAt: new Date(updatedAgent.updatedAt),
          };
        }
        return;
      }

      throw new Error('Failed to activate agent via backend');
    } catch (error) {
      console.error('Failed to activate agent via backend:', error);
      throw error;
    }
  }

  async deactivateAgent(id: string): Promise<void> {
    const agent = this.agents.find((a) => a.id === id);
    if (agent?.isDefault) {
      throw new Error('Cannot deactivate default agent');
    }

    // Backend is REQUIRED for agent deactivation
    if (!this.isBackendAvailable) {
      throw new Error('Backend unavailable - cannot deactivate agents. Please check backend connection.');
    }

    try {
      const response = await fetch(`${getBackendUrl()}/agents/${id}/deactivate`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
      });

      if (response.ok) {
        const updatedAgent = await this.handleResponse<Agent>(response);
        const index = this.agents.findIndex((agent) => agent.id === id);
        if (index !== -1) {
          this.agents[index] = {
            ...updatedAgent,
            installedAt: new Date(updatedAgent.installedAt),
            updatedAt: new Date(updatedAgent.updatedAt),
          };
        }
        return;
      }

      throw new Error('Failed to deactivate agent via backend');
    } catch (error) {
      console.error('Failed to deactivate agent via backend:', error);
      throw error;
    }
  }

  async testConnection(id: string): Promise<boolean> {
    const agent = await this.getAgent(id);
    if (!agent) {
      throw new Error('Agent not found');
    }

    // In production, this would actually test the agent connection
    // For now, simulate a test
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(agent.status === 'active');
      }, 500);
    });
  }

  async exportAgent(
    agentId: string,
    options: { includeSecrets: boolean }
  ): Promise<import('./agentExportImport/schema').ExportEnvelope> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/agents/export/${agentId}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ includeSecrets: options.includeSecrets }),
    });
    return this.handleResponse(response);
  }

  async importAgent(args: {
    payload: import('./agentExportImport/schema').ExportEnvelope;
    mode: 'create' | 'update';
    targetAgentId?: string;
    secretOverrides?: Record<string, string>;
  }): Promise<Agent> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${getBackendUrl()}/agents/import`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        payload: args.payload,
        mode: args.mode,
        targetAgentId: args.targetAgentId,
        secretOverrides: args.secretOverrides ?? {},
      }),
    });
    return this.handleResponse(response);
  }

  async executeAgent(
    agentId: string,
    prompt: string,
    _context: AgentExecutionContext
  ): Promise<AgentResponse> {
    const agent = await this.getAgent(agentId);
    if (!agent) {
      throw new Error('Agent not found');
    }

    if (agent.status !== 'active') {
      throw new Error('Agent is not active');
    }

    // In production, this would route to the appropriate agent framework
    // For now, return a mock response
    return {
      content: `Response from ${agent.name}: ${prompt}`,
      metadata: {
        agentId,
        framework: agent.framework,
        timestamp: new Date().toISOString(),
      },
    };
  }
}

// Singleton instance
export const agentService = new AgentService();

// Helper functions
export async function initializeAgentService(): Promise<void> {
  await agentService.initialize();
}

export async function getAvailableAgents(): Promise<Agent[]> {
  return agentService.getAllAgents();
}

export async function getActiveAgents(): Promise<Agent[]> {
  const agents = await agentService.getAllAgents();
  return agents.filter((agent) => agent.status === 'active');
}
